import { $isLinkNode } from "@lexical/link";
import { $getSelection, $isNodeSelection, $isRangeSelection } from "lexical";
import { useCallback } from "react";
import { useFlag } from "../../../../../../server/optimizely";
import { $isDesignHuddleNode } from "../../../nodes/design-huddle-node";
import { $isImageNode } from "../../../nodes/image-node";
import { $isSurveyLinkNode } from "../../../nodes/survey-link-node";

import type { EditorModalState } from "../../../lexical-context";
import type { BaseSelection, LexicalEditor } from "lexical";

export type EditorLinkData =
  | { mode: "add"; url: undefined; type: "image" | "range" | "survey_link" }
  | { mode: "add"; url: string; type: "asset" }
  | { mode: "edit"; url: string; type: "image" | "range" | "survey_link" };

export const linkButtonSelectionType = (
  selection: ReturnType<typeof $getSelection>,
): "image" | "range" | "survey_link" | null => {
  if (!selection) return null;

  if ($isSurveyLinkNode(selection.getNodes()?.[0]?.getParent())) {
    // If the selection is a survey link node, return "survey_link"
    return "survey_link";
  }

  if ($isRangeSelection(selection)) {
    return "range";
  }

  if ($isNodeSelection(selection)) {
    const nodes = selection.getNodes();
    return nodes.length === 1 &&
      ($isImageNode(nodes[0]) ||
        ($isDesignHuddleNode(nodes[0]) && nodes[0].__contentType === "image"))
      ? "image"
      : null;
  }

  return null;
};

export const getSelectionCurrentLink = (
  selection: BaseSelection,
  selectionType: "image" | "range" | "survey_link",
):
  | {
      mode: "add";
      url: undefined;
      type: "image" | "range" | "survey_link";
    }
  | {
      mode: "edit";
      url: string;
      type: "image" | "range" | "survey_link";
    } => {
  const selectionNodes = selection.getNodes();

  // If the current selection is totally enclosed by a link node, get the link node
  if (selectionNodes.length === 1) {
    if ($isLinkNode(selectionNodes[0]))
      return { mode: "edit", url: selectionNodes[0].getURL(), type: selectionType };

    if ($isDesignHuddleNode(selectionNodes[0]) || $isImageNode(selectionNodes[0])) {
      const href = selectionNodes[0].getHref();
      if (href) return { mode: "edit", url: href, type: selectionType };
    }

    const parent = selectionNodes[0]!.getParent();
    if ($isLinkNode(parent)) return { mode: "edit", url: parent.getURL(), type: selectionType };
  }

  // Othwerwise, if any of the nodes in the selection is a link node, get the first one
  const linkNode = selectionNodes.find($isLinkNode);
  if (linkNode)
    return {
      mode: "edit",
      url: linkNode.getURL(),
      type: selectionType,
    };

  // Otherwise, check if any of the nodes in the selection has a link node as a parent
  const linkParent = selectionNodes.map((x) => x.getParent()).find($isLinkNode);
  if (linkParent)
    return {
      mode: "edit",
      url: linkParent.getURL(),
      type: selectionType,
    };

  const textContent = selection.getTextContent();
  return textContent.trim().length === 0
    ? { mode: "add", url: undefined, type: selectionType }
    : { mode: "edit", url: "", type: selectionType };
};
export const useLinkButtonOnClick = (
  editor: LexicalEditor,
  onClick: (data: EditorModalState) => void,
): (() => void) => {
  const [editor_focus_fix] = useFlag("cha_4472_editor_focus_fix_for_links_and_surveys");

  return useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();

      // The editor mounts without autofocus, so there is no selection until the user clicks
      // into the body. Open the dialog anyway: INSERT_LINK_COMMAND / INSERT_SURVEY_LINK_COMMAND
      // place the caret at the start of the message on confirm.
      if (!selection) {
        if (!editor_focus_fix) return;

        onClick({ type: "link", data: { mode: "add", url: undefined, type: "range" } });
        return;
      }

      const selectionType = linkButtonSelectionType(selection);
      if (!selectionType) return;

      const onClickProps = getSelectionCurrentLink(selection, selectionType);
      onClick({ type: "link", data: onClickProps });
    });
  }, [editor, onClick, editor_focus_fix]);
};
